import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { useTranslation } from "../../i18n";
import FlowLoader from "../FlowLoader";
import { apiFetch } from "../../api/apiFetch";
import { useReadOnly } from "../../access/ReadOnly";
import { evictPointsSettings } from "../gamification/pointsSettings";
import {
    DEFAULT_DURATION_MS,
    KNOWN_POS,
    ScalarTimerEditor,
    SettingBlock,
    TimerMapPerWarehouse,
    sorter2DefaultsFor,
    type ScalarTimerField,
    type TimerMapField,
    type TimerSettingsRow,
} from "../settings-shared/timerEditors";
import "../../styles/admin-settings.css";

// Every column the Targets tab reads or writes. Points targets used to be
// spread across four settings sections plus one buried inside the sorting bay
// manager, which meant nobody could see what "good" looked like across the
// warehouse without opening five accordions.
interface PointsSettingsRow extends TimerSettingsRow {
    warehouse: string;
    book_in_po_timers: Record<string, number>;
    scan_sheet_po_timers: Record<string, number>;
    sorter_2_location_timers: Record<string, number>;
    sorter_1_timer_ms: number | null;
    pick_and_sort_timer_ms: number | null;
    put_away_timer_ms: number | null;
    picking_timer_light_ms: number | null;
    picking_timer_pick_and_sort_ms: number | null;
    picking_timer_large_ms: number | null;
    drop_timer_shelf_ms: number | null;
    drop_timer_forecast_ms: number | null;
    dispatch_timer_light_ms: number | null;
    dispatch_timer_pick_and_sort_ms: number | null;
    dispatch_timer_large_ms: number | null;
    wipe_timer_ms: number | null;
    points_celebration_sound: boolean;
}

/** Placeholder for the flows that had a hardcoded 6s target in the page. */
const SIX_SECONDS = 6000;

/**
 * Warehouse > Points > Targets.
 *
 * A target is what a point is worth: beat it on a unit of work and you bank
 * the seconds you saved. Blank means the flow does not score at all — which is
 * the default for everything added here, so a floor opts in deliberately
 * rather than discovering one morning that it is being measured.
 */
const PointsTargetsAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [rows, setRows] = useState<PointsSettingsRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [pendingKey, setPendingKey] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // Visible warehouses only — the same list every other picker
                // in the admin UI uses, so hiding a warehouse removes it here
                // too without a second switch to remember.
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: PointsSettingsRow[] = await res.json();
                if (alive) setRows(data);
            } catch {
                if (alive) toast.error(t("server_error"), { toastId: "points-targets-load-err" });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [t]);

    // Editors own their own PATCH and hand the saved value back. Mirroring it
    // into `rows` keeps the dirty checks honest, and evicting the cached copy
    // means the shop floor picks up a changed target on its next page load
    // rather than at the end of the shift.
    const updateTimerMap = (field: TimerMapField) =>
        (warehouse: string, timers: Record<string, number>) => {
            setRows(curr => curr.map(r => r.warehouse === warehouse ? { ...r, [field]: timers } : r));
            evictPointsSettings(warehouse);
        };

    const updateScalarTimer = (field: ScalarTimerField) =>
        (warehouse: string, value: number | null) => {
            setRows(curr => curr.map(r => r.warehouse === warehouse ? { ...r, [field]: value } : r));
            evictPointsSettings(warehouse);
        };

    const toggleSound = async (warehouse: string, next: boolean) => {
        const key = `${warehouse}:sound`;
        const prev = rows;
        setPendingKey(key);
        setRows(curr => curr.map(r =>
            r.warehouse === warehouse ? { ...r, points_celebration_sound: next } : r));
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ points_celebration_sound: next }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            evictPointsSettings(warehouse);
        } catch {
            setRows(prev);
            toast.error(t("server_error"), { toastId: `points-sound-err-${warehouse}` });
        } finally {
            setPendingKey(null);
        }
    };

    if (loading) return <FlowLoader />;

    /** A scalar target row, with the flow's name and why it exists. */
    const target = (titleKey: string, field: ScalarTimerField, placeholderMs: number) => (
        <SettingBlock title={t(titleKey)}>
            <ScalarTimerEditor
                rows={rows}
                field={field}
                placeholderMs={placeholderMs}
                onSaved={updateScalarTimer(field)}
            />
        </SettingBlock>
    );

    return (
        <div className="admin-settings-section-body">
            <p className="admin-settings-section-hint">{t("points_targets_hint")}</p>

            <details id="points-goods-in" className="admin-settings-subsection">
                <summary className="admin-settings-subsection-summary">
                    <h3 className="admin-settings-section-title">{t("goods_in_settings")}</h3>
                </summary>
                <div className="admin-settings-subsection-body">
                    <SettingBlock title={t("scan_sheet_settings")}>
                        <p className="admin-settings-section-hint">{t("po_timer_targets_hint")}</p>
                        <TimerMapPerWarehouse
                            rows={rows}
                            field="scan_sheet_po_timers"
                            keysFor={() => KNOWN_POS}
                            getPlaceholder={() => DEFAULT_DURATION_MS}
                            onSaved={updateTimerMap("scan_sheet_po_timers")}
                        />
                    </SettingBlock>
                    <SettingBlock title={t("book_in_settings")}>
                        <p className="admin-settings-section-hint">{t("po_timer_targets_hint")}</p>
                        <TimerMapPerWarehouse
                            rows={rows}
                            field="book_in_po_timers"
                            keysFor={() => KNOWN_POS}
                            getPlaceholder={() => DEFAULT_DURATION_MS}
                            onSaved={updateTimerMap("book_in_po_timers")}
                        />
                    </SettingBlock>
                </div>
            </details>

            <details id="points-put-away" className="admin-settings-subsection">
                <summary className="admin-settings-subsection-summary">
                    <h3 className="admin-settings-section-title">{t("put_away_settings")}</h3>
                </summary>
                <div className="admin-settings-subsection-body">
                    <p className="admin-settings-section-hint">{t("points_target_put_away_hint")}</p>
                    {target("default_timer_target", "put_away_timer_ms", DEFAULT_DURATION_MS)}
                </div>
            </details>

            <details id="points-sorting" className="admin-settings-subsection">
                <summary className="admin-settings-subsection-summary">
                    <h3 className="admin-settings-section-title">{t("sorting_settings")}</h3>
                </summary>
                <div className="admin-settings-subsection-body">
                    {target("sorter_1_settings", "sorter_1_timer_ms", SIX_SECONDS)}
                    <SettingBlock title={t("sorter_2_settings")}>
                        <p className="admin-settings-section-hint">{t("location_timer_targets_hint")}</p>
                        <TimerMapPerWarehouse
                            rows={rows}
                            field="sorter_2_location_timers"
                            keysFor={(warehouse) => Object.keys(sorter2DefaultsFor(warehouse))}
                            getPlaceholder={(warehouse, key) => sorter2DefaultsFor(warehouse)[key] ?? SIX_SECONDS}
                            onSaved={updateTimerMap("sorter_2_location_timers")}
                        />
                    </SettingBlock>
                    <SettingBlock title={t("pick_and_sort")}>
                        {/* Same column the moving-head light reads for its
                            hurry-up cue, so this number is doing two jobs. */}
                        <p className="admin-settings-section-hint">{t("pas_time_target_hint")}</p>
                        <ScalarTimerEditor
                            rows={rows}
                            field="pick_and_sort_timer_ms"
                            placeholderMs={SIX_SECONDS}
                            onSaved={updateScalarTimer("pick_and_sort_timer_ms")}
                        />
                    </SettingBlock>
                </div>
            </details>

            <details id="points-picking" className="admin-settings-subsection">
                <summary className="admin-settings-subsection-summary">
                    <h3 className="admin-settings-section-title">{t("picking_settings")}</h3>
                </summary>
                <div className="admin-settings-subsection-body">
                    <p className="admin-settings-section-hint">{t("points_target_picking_hint")}</p>
                    {/* Stored as pick_type 'light'; called Single everywhere a
                        human reads it. */}
                    {target("picking_light_settings", "picking_timer_light_ms", 0)}
                    {target("picking_pick_and_sort_settings", "picking_timer_pick_and_sort_ms", 0)}
                    {target("points_flow_large", "picking_timer_large_ms", 0)}
                    {target("picking_shelf_drop_settings", "drop_timer_shelf_ms", 0)}
                    {target("picking_forecast_drop_settings", "drop_timer_forecast_ms", 0)}
                </div>
            </details>

            <details id="points-dispatch" className="admin-settings-subsection">
                <summary className="admin-settings-subsection-summary">
                    <h3 className="admin-settings-section-title">{t("dispatch_settings")}</h3>
                </summary>
                <div className="admin-settings-subsection-body">
                    {/* Per pick type: a Large order and a Single are not the
                        same job at the bench, so one target for both would
                        make one of them meaningless. */}
                    <p className="admin-settings-section-hint">{t("points_target_dispatch_hint")}</p>
                    {target("picking_light_settings", "dispatch_timer_light_ms", 0)}
                    {target("picking_pick_and_sort_settings", "dispatch_timer_pick_and_sort_ms", 0)}
                    {target("points_flow_large", "dispatch_timer_large_ms", 0)}
                </div>
            </details>

            <details id="points-wipe" className="admin-settings-subsection">
                <summary className="admin-settings-subsection-summary">
                    <h3 className="admin-settings-section-title">{t("wipe_settings")}</h3>
                </summary>
                <div className="admin-settings-subsection-body">
                    <p className="admin-settings-section-hint">{t("points_target_wipe_hint")}</p>
                    {target("default_timer_target", "wipe_timer_ms", 0)}
                </div>
            </details>

            <details id="points-celebrations" className="admin-settings-subsection">
                <summary className="admin-settings-subsection-summary">
                    <h3 className="admin-settings-section-title">{t("points_celebrations")}</h3>
                </summary>
                <div className="admin-settings-subsection-body">
                    <p className="admin-settings-section-hint">{t("points_celebration_sound_hint")}</p>
                    <div className="admin-settings-table">
                        <div className="admin-settings-row admin-settings-row--head">
                            <div>{t("warehouse_col")}</div>
                            <div>{t("points_celebration_sound")}</div>
                        </div>
                        {rows.map(r => (
                            <div key={r.warehouse} className="admin-settings-row">
                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                <div>
                                    <label className="admin-settings-toggle">
                                        <input
                                            type="checkbox"
                                            checked={r.points_celebration_sound !== false}
                                            disabled={pendingKey === `${r.warehouse}:sound` || readOnly}
                                            aria-disabled={readOnly}
                                            title={readOnly ? t("read_only_mode") : undefined}
                                            onChange={e => toggleSound(r.warehouse, e.target.checked)}
                                        />
                                        <span className="admin-settings-slider" />
                                    </label>
                                </div>
                            </div>
                        ))}
                        {rows.length === 0 && (
                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                        )}
                    </div>
                </div>
            </details>
        </div>
    );
};

export default PointsTargetsAdmin;
